import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { StatusDot } from "../components/chips";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { num } from "../lib/format";
import { enemiesQuery } from "../lib/queries";

export const Route = createFileRoute("/monsters/")({ component: Monsters });

function Monsters() {
	const enemies = useQuery(enemiesQuery);
	if (enemies.isPending) return <Loading what="Loading monsters" />;
	if (enemies.isError) return <LoadError error={enemies.error} retry={() => void enemies.refetch()} />;

	return (
		<div>
			<PageHeader title="Monsters" eyebrow={`${enemies.data.length} types in enemies.json`} />
			<p className="mb-6 max-w-2xl" style={{ color: "var(--color-ink-300)" }}>
				Base stats before any scaling. A chapter page shows what these become at a given stage. Open a monster to write its lore and
				notes.
			</p>
			<div className="panel overflow-x-auto">
				<table className="table">
					<thead>
						<tr>
							<th>Monster</th>
							<th>Appears</th>
							<th className="num">HP</th>
							<th className="num">ATK</th>
							<th className="num">DEF</th>
							<th className="num">XP</th>
							<th className="num">Coins</th>
							<th>Notes</th>
						</tr>
					</thead>
					<tbody>
						{enemies.data.map((e) => {
							const bossIn = e.roles.filter((r) => r.role === "boss").map((r) => r.chapter_index);
							const miniIn = e.roles.filter((r) => r.role === "mini_boss").map((r) => r.chapter_index);
							const poolIn = e.roles.filter((r) => r.role === "pool").map((r) => r.chapter_index);
							return (
								<tr key={e.id}>
									<td>
										<Link to="/monsters/$id" params={{ id: e.id }} className="font-medium underline decoration-[var(--color-ink-600)] underline-offset-2 hover:decoration-[var(--color-gold-400)]">
											{e.name}
										</Link>
										<div className="font-mono text-xs" style={{ color: "var(--color-ink-400)" }}>
											{e.id}
											{e.cc_immune && " · CC immune"}
										</div>
									</td>
									<td className="text-xs" style={{ color: "var(--color-ink-300)" }}>
										{poolIn.length > 0 && <div>Waves: ch. {ranges(poolIn)}</div>}
										{miniIn.length > 0 && <div>Mini-boss: ch. {ranges(miniIn)}</div>}
										{bossIn.length > 0 && <div>Boss: ch. {ranges(bossIn)}</div>}
										{e.roles.length === 0 && <span style={{ color: "var(--color-ink-400)" }}>Unused</span>}
									</td>
									<td className="num">{num(e.hp)}</td>
									<td className="num">{num(e.atk, 1)}</td>
									<td className="num">{num(e.def, 2)}</td>
									<td className="num">{e.xp}</td>
									<td className="num">{e.coins}</td>
									<td>
										<div className="flex items-center gap-2">
											<StatusDot status={e.annotation.status} label={false} />
											<span className="truncate text-sm" style={{ color: e.annotation.summary ? undefined : "var(--color-ink-400)" }}>
												{e.annotation.summary || "Nothing written"}
											</span>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}

/** [1,2,3,5,6] → "1–3, 5–6" */
function ranges(sorted: number[]): string {
	const out: string[] = [];
	let start = sorted[0];
	let prev = sorted[0];
	for (const n of sorted.slice(1)) {
		if (n === (prev ?? 0) + 1) {
			prev = n;
			continue;
		}
		out.push(start === prev ? String(start) : `${start}–${prev}`);
		start = prev = n;
	}
	if (start !== undefined) out.push(start === prev ? String(start) : `${start}–${prev}`);
	return out.join(", ");
}
